/**
 * Whether a directory number would actually reach someone by SMS.
 *
 * Mirrors the rule the server applies before handing anything to Twilio
 * (utils/phone.go — FormatPhoneNumber / CanReceiveSMS). Kept in step on purpose:
 * a number too mangled to dial otherwise reads as "on file" in the composer,
 * counts towards "send to 14", and then gets silently dropped at send time. The
 * two answers have to be the same answer.
 *
 * Numbers themselves are never rendered anywhere in this app; this only ever
 * feeds a reachable / not-reachable indicator.
 */
export function canReceiveSMS(phone?: string | null): boolean {
  if (!phone) return false

  const cleaned = phone.replace(/[^\d+]/g, '').replace(/^\+/, '')

  // 10 digits (North America), 11 starting with 1 (same, with country code), or
  // anything longer (international). Shorter than that isn't a phone number —
  // it's usually an internal extension typed into the mobile field.
  if (cleaned.length === 10) return true
  if (cleaned.length === 11 && cleaned.startsWith('1')) return true
  return cleaned.length > 10
}

/**
 * Two directory numbers written differently — "(515) 555-0111", "5155550111",
 * "+1 515 555 0111" — reduced to the same key, so a roster can be checked for
 * someone without depending on how their number was typed.
 *
 * Deliberately loose: it only has to be stable enough to answer "is this person
 * already in this chat", never to dial. Returns "" for anything that isn't a
 * usable number, which never matches another "".
 */
export function phoneKey(phone?: string | null): string {
  if (!canReceiveSMS(phone)) return ''
  const digits = (phone ?? '').replace(/\D/g, '')
  // Drop a North American country code so +1XXXXXXXXXX and XXXXXXXXXX agree.
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
}
